import React, { useContext, useState } from "react";
import { IGateway } from "@grabcad/company-server-shared/dist/types";

import StatusBadge from "./StatusBadge/StatusBadge";
import LinkedDevices from "./LinkedDevices/LinkedDevices";
import DataPrivacySettings from "./DataPrivacySettings/DataPrivacySettings";
import GatewayActions, { GatewayActionItem } from "./GatewayActions/GatewayActions";
import DownloadInstallerModal from "./DownloadInstallerModal/DownloadInstallerModal";
import RemoveGatewayModal from "./RemoveGatewayModal/RemoveGatewayModal";

import {
    Card,
    Header,
    HeaderLeft,
    HeaderRight,
    Title,
    GatewayId,
    TooltipWrapper,
    Tooltip,
    Divider,
} from "./GatewayCard.styles";

import {
    deleteGatewayInstaller,
    downloadGatewayInstaller,
    decommissionPrinter,
} from "../../GatewayAction";
import { ApplicationContext } from "../../../../../components/ApplicationProvider";
import { Notifier } from "../../../../../view/Notifier";
import DecommissionPrinterModal from "./DecommissionPrinterModal/DecommissionPrinterModal";

type GatewayCardProps = {
    gateway: IGateway;
    printerSerial?: string;
    onGatewayRemoved?: (coreDeviceThingName: string) => void;
};

const GatewayCard: React.FC<GatewayCardProps> = ({ gateway, printerSerial, onGatewayRemoved }) => {
    const { t } = useContext(ApplicationContext);

    const [isDownloadInstallerOpen, setIsDownloadInstallerOpen] = useState(false);

    const [isRemoveGatewayOpen, setIsRemoveGatewayOpen] = useState(false);

    const [isDeletingGateway, setIsDeletingGateway] = useState(false);

    const [isDecommissionPrinterOpen, setIsDecommissionPrinterOpen] = useState(false);

    const [isDecommissioningPrinter, setIsDecommissioningPrinter] = useState(false);

    const [selectedPrinterName, setSelectedPrinterName] = useState("");

    const coreDeviceThingName = gateway.coreDeviceThingName;

    /**
     * Open Download Installer confirmation modal.
     */
    const handleDownloadInstaller = (): void => {
        setIsDownloadInstallerOpen(true);
    };

    /**
     * Close Download Installer confirmation modal.
     */
    const handleCloseDownloadInstaller = (): void => {
        setIsDownloadInstallerOpen(false);
    };

    /**
     * Download Gateway Installer.
     */
    const handleConfirmDownload = async (): Promise<void> => {
        if (!coreDeviceThingName) {
            return;
        }

        await downloadGatewayInstaller({
            coreDeviceThingName,
            printerSerial,
            t,
        });

        setIsDownloadInstallerOpen(false);
    };

    /**
     * Open Remove Gateway confirmation modal.
     */
    const handleRemoveGateway = (): void => {
        setIsRemoveGatewayOpen(true);
    };

    /**
     * Close Remove Gateway confirmation modal.
     */
    const handleCancelRemoveGateway = (): void => {
        if (isDeletingGateway) {
            return;
        }

        setIsRemoveGatewayOpen(false);
    };

    /**
     * Delete Gateway after confirmation.
     */
    const handleConfirmRemoveGateway = async (): Promise<void> => {
        if (!coreDeviceThingName || isDeletingGateway) {
            return;
        }

        setIsDeletingGateway(true);

        try {
            await deleteGatewayInstaller(coreDeviceThingName, printerSerial);

            setIsRemoveGatewayOpen(false);

            Notifier.success(t("gateways.remove_gateway_success"));

            // This removes the card from GatewayCardGrid.
            onGatewayRemoved?.(coreDeviceThingName);
        } catch {
            Notifier.fail(t("gateways.remove_gateway_error"));
        } finally {
            setIsDeletingGateway(false);
        }
    };

    /**
     * Closes Decommission Printer confirmation modal.
     */
    const handleCancelDecommissionPrinter = (): void => {
        if (isDecommissioningPrinter) {
            return;
        }

        setIsDecommissionPrinterOpen(false);
    };

    /**
     * Decommissions the selected printer.
     */
    const handleConfirmDecommissionPrinter = async (): Promise<void> => {
        if (!selectedPrinterName || isDecommissioningPrinter) {
            return;
        }

        setIsDecommissioningPrinter(true);

        try {
            await decommissionPrinter(selectedPrinterName, printerSerial);

            setIsDecommissionPrinterOpen(false);

            Notifier.success(t("gateways.remove_printer_success"));

            // TODO:
            // Refresh the gateway data or remove the printer locally
            // after successful decommission.
        } catch {
            Notifier.fail(t("gateways.remove_printer_error"));
        } finally {
            setIsDecommissioningPrinter(false);
        }
    };

    const actions: GatewayActionItem[] = [
        {
            key: "download-installer",
            text: t("devices.gateways.gateway_card.download_installer"),
            icon: "download",
            onClick: handleDownloadInstaller,
        },
        {
            key: "remove-gateway",
            text: t("gateways.remove_gateway"),
            icon: "trash alternate",
            onClick: handleRemoveGateway,
        },
    ];

    return (
        <>
            <Card data-testid="gateway-card">
                <Header data-testid="gateway-card-header">
                    <HeaderLeft data-testid="gateway-card-header-left">
                        <TooltipWrapper>
                            <Title data-testid="gateway-card-title">{gateway.site}</Title>
                            <Tooltip data-testid="gateway-site-tooltip">Gateway Site</Tooltip>
                        </TooltipWrapper>
                        <TooltipWrapper>
                            <GatewayId data-testid="gateway-card-id">
                                {coreDeviceThingName}
                            </GatewayId>
                            <Tooltip data-testid="gateway-thing-name-tooltip">
                                Gateway ThingName
                            </Tooltip>
                        </TooltipWrapper>
                    </HeaderLeft>
                    <HeaderRight data-testid="gateway-card-header-right">
                        <StatusBadge status={gateway.status} />
                        <GatewayActions actions={actions} />
                    </HeaderRight>
                </Header>
                <Divider />
                <LinkedDevices
                    devices={gateway.associatedClientDevices}
                    onDelete={(device) => {
                        setSelectedPrinterName(device.thingName);
                        setIsDecommissionPrinterOpen(true);
                    }}
                />
                <Divider />
                <DataPrivacySettings
                    initialValue="Default"
                    readOnly
                    // TODO: Re-enable editing here once gateway privacy persistence is wired in.
                    onToggle={(value) => console.log("Privacy changed", coreDeviceThingName, value)}
                />
            </Card>
            <DownloadInstallerModal
                open={isDownloadInstallerOpen}
                onClose={handleCloseDownloadInstaller}
                onDownload={handleConfirmDownload}
            />
            <RemoveGatewayModal
                open={isRemoveGatewayOpen}
                gatewayName={gateway.site}
                isDeleting={isDeletingGateway}
                onCancel={handleCancelRemoveGateway}
                onDelete={handleConfirmRemoveGateway}
            />
            <DecommissionPrinterModal
                open={isDecommissionPrinterOpen}
                printerName={selectedPrinterName}
                isDecommissioning={isDecommissioningPrinter}
                onCancel={handleCancelDecommissionPrinter}
                onDecommission={handleConfirmDecommissionPrinter}
            />
        </>
    );
};

export default GatewayCard;
